import { ActivityTypeEnum } from "@/lib/enum/activity-type.enum";
import { ContractTypeEnum } from "@/lib/enum/contract-type.enum";
import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import { ICustomViewableEntity } from "@/lib/interfaces/custom-viewable-entity.interface";
import { IVSelectItem } from "@/lib/interfaces/v-select-item.interface";
import { ContractGoToHelper } from "@/lib/utility/contract.go-to-helper";
import { IEntity } from "@/lib/utility/data/entity.interface";
import { UpdateDto } from "@/lib/utility/data/update-dto.interface";
import { formatYearsMonthDay } from "@/lib/utility/date-helper";
import { GroupGoToHelper } from "@/lib/utility/group.go-to-helper";
import { $t } from "@/lib/utility/t";
import contractService from "@/services/mrfiktiv/services/contract-service";
import {
  MrfiktivContractControllerCreatePayloadGen,
  MrfiktivContractControllerUpdatePayloadGen,
  MrfiktivContractViewModelGen,
  MrfiktivCustomerContractViewModelGen,
  MrfiktivInsuranceContractViewModelGen,
  MrfiktivWorkshopContractViewModelGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { BackendResourceEnum, ResourceEnum } from "@/store/enum/authResourceEnum";
import { ContractDataAccessLayer } from "@/store/modules/access-layers/contract.access-layer";
import { GroupDataAccessLayer } from "@/store/modules/access-layers/group.access-layer";
import { SignDocumentDataAccessLayer } from "@/store/modules/access-layers/sign-document.access-layer";
import { PartnerUserModule } from "@/store/modules/partner-user.store";
import VueRouter from "vue-router";
import { ActivityLog } from "./activity-log.entity";
import { ClientReference } from "./client-reference.entity";
import { CustomFieldValue, ICustomFieldValue } from "./custom-field-value.entity";
import { CustomerContractRate, ICustomerContractRate } from "./customer-contract-rate.entity";
import { IInsuranceContractRate, InsuranceContractRate } from "./insurance-contract-rate.entity";
import { ProviderReference } from "./provider-reference.entity";
import { IReference, Reference } from "./reference.entity";
import { ITimestamp, Timestamp } from "./timestamp.entity";
import { IWorkshopContractRate, WorkshopContractRate } from "./workshop-contract-rate.entity";
import { Company, ICompany } from "./company.entity";
import { CurrencyEnum } from "@/lib/enum/currency.enum";
import { isDateRangeActive } from "@/lib/utility/is-date-range-active";

@IsFilterable
export class ContractBase implements Omit<
  | IEntity<IContract, MrfiktivContractControllerUpdatePayloadGen>
  | ICustomViewableEntity<IContract, MrfiktivContractControllerUpdatePayloadGen>
  | Omit<MrfiktivContractViewModelGen | MrfiktivWorkshopContractViewModelGen, "values">
  | UpdateDto<IContract>,
  "fetch"
> {
  /** Id of the contract object */
  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.contract.id"
  })
  id: string;

  /** The partnerId of the contract */
  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.contract.partnerId"
  })
  partnerId: string;

  @FilterConfig({
    displayName: "objects.company.assignees",
    type: FilterTypes.OBJECT_ID,
    width: "120",
    config: {
      itemCallback: () => PartnerUserModule.paginationList,
      mapItemToComponent: (item) => ({ item }),
      itemValue: "id",
      component: "refs-user"
    }
  })
  assignees?: string[];

  /** The creator */
  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.contract.userId",
    config: {
      itemCallback: () => PartnerUserModule.paginationList,
      mapItemToComponent: (item: any) => {
        return { item };
      },
      itemValue: "id",
      component: "refs-user"
    }
  })
  userId: string;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.contract.title"
  })
  title?: string;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.contract.description"
  })
  description?: string;

  @FilterConfig({
    type: FilterTypes.ENUM,
    config: {
      items: Object.values(ContractTypeEnum).map((v) => {
        return {
          text: `enums.ContractTypeEnum.${v}`,
          value: v
        } as IVSelectItem;
      }),
      itemValue: "value"
    },
    displayName: "objects.contract.type"
  })
  type: ContractTypeEnum;

  get startDateFormable(): string {
    if (!this.startDate) {
      return "";
    }
    return formatYearsMonthDay(new Date(this.startDate));
  }
  set startDateFormable(date: string) {
    if (date) {
      this.startDate = new Date(date).toISOString();
    }

    this.startDate = date;
  }

  @FilterConfig({
    type: FilterTypes.DATE,
    displayName: "objects.contract.startDate"
  })
  startDate?: string | undefined;

  get endDateFormable(): string {
    if (!this.endDate) {
      return "";
    }
    return formatYearsMonthDay(new Date(this.endDate));
  }
  set endDateFormable(date: string) {
    if (date) {
      this.endDate = new Date(date).toISOString();
    }

    this.endDate = date;
  }

  @FilterConfig({
    type: FilterTypes.DATE,
    displayName: "objects.contract.endDate"
  })
  endDate?: string | undefined;

  @FilterConfig({
    type: ProviderReference
  })
  providerIds: IReference[];

  @FilterConfig({
    type: ClientReference
  })
  clientIds: IReference[];

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.contract.groupId",
    config: {
      itemCallback: () => GroupDataAccessLayer.entities?.filter((e) => e.type === BackendResourceEnum.CONTRACT) ?? [],
      mapItemToComponent: (item: any) => ({ item }),
      itemValue: "id",
      component: "refs-group"
    }
  })
  groupId?: string;

  /** values of the custom fields */
  @FilterConfig({ type: CustomFieldValue })
  values: ICustomFieldValue[] = [];

  get timestampCreatedReadable() {
    return this.timestamp.createdReadable;
  }
  set timestampCreatedReadable(_: string) {
    // do nothing
  }

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.contract.documentIds",
    config: {
      itemCallback: () => SignDocumentDataAccessLayer.entities,
      mapItemToComponent: (item) => ({ item }),
      itemValue: "id",
      component: "refs-document"
    }
  })
  documentIds: string[];

  /** refs */
  @FilterConfig({
    type: Reference
  })
  refs: IReference[];

  @FilterConfig({
    type: WorkshopContractRate
  })
  @FilterConfig({
    type: InsuranceContractRate
  })
  @FilterConfig({
    type: CustomerContractRate
  })
  details?: IWorkshopContractRate | IInsuranceContractRate | ICustomerContractRate;

  /** timestamp */
  @FilterConfig({
    type: Timestamp
  })
  timestamp: ITimestamp;

  isUpdateable = true;

  loading = false;

  _currency?: CurrencyEnum | undefined;

  get currency(): CurrencyEnum | undefined {
    return this._currency || CurrencyEnum.EUR;
  }

  clientCompanies: ICompany[] = [];
  isClientCompaniesLoading = false;

  /**
   * returns first client company
   */
  get company() {
    if (this.clientCompanies.length > 0) {
      return this.clientCompanies[0];
    }
    return undefined;
  }

  /**
   * returns the id of the first client company
   */
  get companyId() {
    if (this.clientCompanies.length > 0) {
      return this.clientCompanies[0].id;
    }
    return undefined;
  }

  get projectId() {
    return this.groupId;
  }

  get titleReadable() {
    if (!this.title) {
      return $t("contract.noTitle");
    }

    return this.title;
  }

  get updateDto(): MrfiktivContractControllerUpdatePayloadGen {
    let dto: MrfiktivContractControllerUpdatePayloadGen = {
      title: this.title,
      description: this.description,
      groupId: this.groupId,
      values: CustomFieldValue.buildCustomFieldValuesDto(this.values || []),
      clientIds: this.clientIds,
      providerIds: this.providerIds,
      documentIds: this.documentIds,
      startDate:
        this.startDate && !isNaN(Date.parse(this.startDate)) ? new Date(this.startDate).toISOString() : this.startDate,
      endDate: this.endDate && !isNaN(Date.parse(this.endDate)) ? new Date(this.endDate).toISOString() : this.endDate,
      assignees: this.assignees,
      refs: Reference.filterDuplicates((this.refs ?? []) as IReference[])
    };

    if (this.type === ContractTypeEnum.WORKSHOP) {
      dto = { ...dto, details: new WorkshopContractRate(this.details as any) };
    } else if (this.type === ContractTypeEnum.INSURANCE) {
      dto = { ...dto, details: new InsuranceContractRate(this.details as any) };
    } else if (this.type === ContractTypeEnum.CUSTOMER) {
      dto = { ...dto, details: new CustomerContractRate(this.details as any) };
    }

    return dto;
  }

  get createDto(): MrfiktivContractControllerCreatePayloadGen {
    return {
      ...(this.updateDto as Omit<MrfiktivContractControllerCreatePayloadGen, "type">),
      title: this.title || undefined,
      description: this.description || undefined,
      groupId: this.groupId || undefined,
      endDate: this.endDateDto,
      startDate: this.startDateDto,
      type: this.type
    };
  }

  /**
   * Returns the start date in ISO format
   */
  get startDateDto(): null | string {
    return this.startDate ? new Date(this.startDate).toISOString() : null;
  }

  /**
   * Returns the end date in ISO format
   */
  get endDateDto(): null | string {
    return this.endDate ? new Date(this.endDate).toISOString() : null;
  }

  /**
   * Determines if a contract is active based on current date
   */
  get isActive(): boolean {
    return isDateRangeActive(this.startDate, this.endDate);
  }

  /**
   * isActive at a specific date
   * @param date
   * @returns
   */
  activeContractAtDate(ref?: string | Date | null): boolean {
    if (!ref) {
      ref = new Date();
    }
    return isDateRangeActive(this.startDate, this.endDate, new Date(ref));
  }

  constructor(data?: Partial<MrfiktivContractViewModelGen> | IContract) {
    this.id = data?.id ?? "";
    this.partnerId = data?.partnerId ?? "";
    this.userId = data?.userId ?? "";
    this.title = data?.title;
    this.description = data?.description;
    this.groupId = data?.groupId;
    this.values = CustomFieldValue.buildCustomFieldValues(data?.values || []);
    this.type = data?.type as ContractTypeEnum;
    this.startDate = data?.startDate;
    this.endDate = data?.endDate;
    this.providerIds = Reference.filterDuplicates(
      (data?.providerIds as IReference[])?.map((ref) => new Reference(ref))
    );
    this.clientIds = Reference.filterDuplicates((data?.clientIds as IReference[])?.map((ref) => new Reference(ref)));
    this.documentIds = data?.documentIds ?? [];
    this.refs = Reference.filterDuplicates((data?.refs ?? []).map((ref) => new Reference(ref)));
    this.assignees = data?.assignees;
    this.timestamp = new Timestamp(data?.timestamp);

    if (data?.type === ContractTypeEnum.WORKSHOP) {
      const workshopContractData = data as MrfiktivWorkshopContractViewModelGen;
      this.details = new WorkshopContractRate(workshopContractData.details);
    } else if (data?.type === ContractTypeEnum.INSURANCE) {
      const insuranceContractData = data as MrfiktivInsuranceContractViewModelGen;
      this.details = new InsuranceContractRate(insuranceContractData.details);
    } else if (data?.type === ContractTypeEnum.CUSTOMER) {
      const customerContractData = data as MrfiktivCustomerContractViewModelGen;
      this.details = new CustomerContractRate(customerContractData.details);
    } else {
      this.details = undefined;
    }
  }

  map(data: MrfiktivContractViewModelGen): void {
    this.id = data?.id ?? "";
    this.partnerId = data?.partnerId ?? "";
    this.userId = data?.userId ?? "";
    this.title = data?.title;
    this.description = data?.description;
    this.groupId = data?.groupId;
    this.values = CustomFieldValue.buildCustomFieldValues(data?.values || []);
    this.type = data?.type as ContractTypeEnum;
    this.startDate = data?.startDate;
    this.endDate = data?.endDate;
    this.providerIds = Reference.filterDuplicates(
      (data?.providerIds as IReference[])?.map((ref) => new Reference(ref))
    );
    this.clientIds = Reference.filterDuplicates((data?.clientIds as IReference[])?.map((ref) => new Reference(ref)));
    this.documentIds = data?.documentIds ?? [];
    this.refs = Reference.filterDuplicates((data?.refs ?? []).map((ref) => new Reference(ref)));
    this.assignees = data?.assignees;
    this.timestamp = new Timestamp(data?.timestamp);

    if (data?.type === ContractTypeEnum.WORKSHOP) {
      const workshopContractData = data as MrfiktivWorkshopContractViewModelGen;
      this.details = new WorkshopContractRate(workshopContractData.details);
    } else if (data?.type === ContractTypeEnum.INSURANCE) {
      const insuranceContractData = data as MrfiktivInsuranceContractViewModelGen;
      this.details = new InsuranceContractRate(insuranceContractData.details);
    } else if (data?.type === ContractTypeEnum.CUSTOMER) {
      const customerContractData = data as MrfiktivCustomerContractViewModelGen;
      this.details = new CustomerContractRate(customerContractData.details);
    } else {
      this.details = (data as any)?.details || {};
    }
  }

  async create(): Promise<this> {
    const res = await contractService.create(this.partnerId, this.createDto);

    this.map(res);
    ContractDataAccessLayer.set(this);

    await new ActivityLog().createCreationActivity({
      partnerId: this.partnerId,
      source: { refId: this.id, refType: BackendResourceEnum.CONTRACT }
    });

    return this;
  }

  async fetch(): Promise<this> {
    this.loading = true;
    try {
      const res = await contractService.getOne(this.partnerId, this.id);
      this.map(res);
      ContractDataAccessLayer.set(this);
    } catch (e) {
      this.loading = false;
      ContractDataAccessLayer.delete(this);
      throw e;
    }
    this.loading = false;

    return this;
  }

  async updatePartial(dto: Partial<MrfiktivContractControllerUpdatePayloadGen>): Promise<this> {
    if (dto.values) {
      dto.values = CustomFieldValue.buildCustomFieldValuesDto((dto.values as ICustomFieldValue[]) || []);
    }

    const res = await contractService.update(this.partnerId, this.id, dto);

    this.map(res);
    ContractDataAccessLayer.set(this);

    return this;
  }

  async update(): Promise<this> {
    this.loading = true;
    try {
      await this.updatePartial(this.updateDto);
    } catch (e) {
      this.loading = false;
      throw e;
    }
    this.loading = false;

    return this;
  }

  async delete(): Promise<void> {
    const res = await contractService.delete(this.partnerId, this.id);

    this.map(res);
    ContractDataAccessLayer.delete(this);
  }

  goTo(router: VueRouter) {
    return {
      customView: (newTab = false) => {
        if (this.groupId)
          new GroupGoToHelper(router).goToGroupTypeCustomView({
            partnerId: this.partnerId,
            type: BackendResourceEnum.CONTRACT,
            groupId: this.groupId,
            viewId: "0",
            newTab
          });
      },
      table: (newTab = false) =>
        new ContractGoToHelper(router).goToContractTable({
          partnerId: this.partnerId,
          newTab
        }),
      detail: (newTab = false) =>
        new ContractGoToHelper(router).goToContractDetail({
          partnerId: this.partnerId,
          contractId: this.id,
          newTab
        }),
      form: (newTab = false) =>
        new ContractGoToHelper(router).goToContractDetailForm({
          partnerId: this.partnerId,
          contractId: this.id,
          newTab
        })
    };
  }

  /**
   * Create an activity log for assignee changes
   * @param newAssignees The ids of the new assignees of the ticket
   * @param activityType Whether the assignee was added or removed
   */
  async createAssigneeActivity(
    activityType: ActivityTypeEnum.CREATE_ASSIGNEE | ActivityTypeEnum.DELETE_ASSIGNEE,
    newAssignees?: string[]
  ) {
    if (!newAssignees?.length) return;

    await new ActivityLog().createAssigneeActivity({
      partnerId: this.partnerId,
      source: {
        refType: ResourceEnum.CONTRACT,
        refId: this.id
      },
      newAssignees,
      activityType
    });
  }

  async fetchCompanyClients() {
    try {
      this.clientCompanies = [];
      this.isClientCompaniesLoading = true;
      if (!this.clientIds || this.clientIds.length === 0) {
        return;
      }

      for (const ref of this.clientIds) {
        if (ref.refType === BackendResourceEnum.COMPANY) {
          const company = await new Company({ id: ref.refId, partnerId: this.partnerId }).fetch();
          this.clientCompanies.push(company);
        }
      }
    } finally {
      this.isClientCompaniesLoading = false;
    }
  }
}

type IContract = ContractBase;
const Contract = Filter.createForClass(ContractBase);

export { Contract, IContract };
